/**
 * Sanitizes HTML content for safe SSR rendering
 * Removes document structure tags and cleans up the content
 */

/**
 * Extracts and processes HTML content for SSR-safe rendering
 * @param {string} htmlString - The HTML string to sanitize
 * @returns {object} - Object containing sanitized content and extracted styles/links
 */
export function sanitizeHtmlForSSR(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') {
    return {
      content: '',
      styles: '',
      externalLinks: [],
    };
  }

  // Extract inline styles from <style> tags BEFORE extracting body
  const styleMatches = htmlString.match(/<style[^>]*>([\s\S]*?)<\/style>/gi) || [];
  const styles = styleMatches
    .map((styleTag) => {
      const match = styleTag.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
      return match ? match[1] : '';
    })
    .join('\n');

  // Extract external stylesheet links BEFORE extracting body
  const linkMatches = htmlString.match(/<link[^>]*rel=["']stylesheet["'][^>]*>/gi) || [];
  const externalLinks = linkMatches.map((linkTag) => {
    const hrefMatch = linkTag.match(/href=["']([^"']+)["']/i);
    return hrefMatch ? hrefMatch[1] : '';
  }).filter(Boolean);

  // Remove everything before <body> and after </body>
  let content = htmlString;

  // Try to extract body content
  const [, bodyContent] = htmlString.match(/<body[^>]*>([\s\S]*?)<\/body>/i) || [];
  if (bodyContent) {
    content = bodyContent;
  } else {
    // If no body tag, remove head section manually
    content = htmlString.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '');
  }

  // Clean up the content - be aggressive
  content = content
    // Remove DOCTYPE, html, head, body tags
    .replace(/<!DOCTYPE[^>]*>/gi, '')
    .replace(/<\/?html[^>]*>/gi, '')
    .replace(/<\/?head[^>]*>/gi, '')
    .replace(/<\/?body[^>]*>/gi, '')
    .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
    // Remove standalone <style> and <link> tags (styles already extracted)
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<link[^>]*>/gi, '')
    // Remove <title> tags
    .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
    // Remove <meta> tags
    .replace(/<meta[^>]*>/gi, '')
    // Remove comments
    .replace(/<!--[\s\S]*?-->/g, '')
    .trim();

  // Fix mismatched div tags by counting and balancing
  const openDivs = (content.match(/<div[^>]*>/gi) || []).length;
  const closeDivs = (content.match(/<\/div>/gi) || []).length;

  if (closeDivs > openDivs) {
    // Remove extra closing divs from the end
    const extraClosing = closeDivs - openDivs;
    let count = 0;
    content = content.replace(/<\/div>/gi, (match) => {
      if (count >= closeDivs - extraClosing) {
        return '';
      }
      count += 1;
      return match;
    });
  }

  // Normalize whitespace
  content = content
    .replace(/\n{3,}/g, '\n\n')
    .replace(/>\s+</g, '><') // Remove whitespace between tags
    .trim();

  return {
    content,
    styles,
    externalLinks,
  };
}

/**
 * Validates HTML structure for common SSR issues
 * @param {string} htmlString - The HTML string to validate
 * @returns {object} - Validation results with warnings
 */
export function validateHtmlForSSR(htmlString) {
  const warnings = [];

  // Check for document structure tags
  if (/<(!DOCTYPE|html|head|body)/i.test(htmlString)) {
    warnings.push('HTML contains document structure tags (DOCTYPE, html, head, body) which should be removed');
  }

  // Check for external resources
  if (/<link[^>]*rel=["']stylesheet["']/i.test(htmlString)) {
    warnings.push('HTML contains external stylesheet links which may not load properly in SSR');
  }

  // Check for inline scripts
  if (/<script/i.test(htmlString)) {
    warnings.push('HTML contains script tags which may cause hydration issues');
  }

  // Check for mismatched tags
  const openDivs = (htmlString.match(/<div[^>]*>/gi) || []).length;
  const closeDivs = (htmlString.match(/<\/div>/gi) || []).length;
  if (openDivs !== closeDivs) {
    warnings.push(`Mismatched div tags: ${openDivs} opening, ${closeDivs} closing`);
  }

  return {
    isValid: warnings.length === 0,
    warnings,
  };
}
